import { put, cancelled, select, call } from 'redux-saga/effects';
import { createAsyncAction, createReducer } from 'typesafe-actions';
import { api } from 'api';
import { getAllCookies, getArticle, getEnv } from 'common/selectors';
import { getRegionId } from 'features/page-model/ducks/get-model';
import log from 'loglevel';
import { EffectR } from 'types/utils';
import { CalculatorParameters } from '../types';
import { LAMINATE_PROJECT_CONFIGURATION_ID_COOKIE_NAME } from '../constants';
import { fetchProductCalculatorParameters } from './api';
import { RESET } from './product-main-info';
import { isCalculatorAvailable } from './selectors';

interface ApiErrorResponse {
  status: number;
  statusText?: string;
}

function isErrorResponse(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'status' in response &&
    typeof (response as ApiErrorResponse).status === 'number' &&
    (response as ApiErrorResponse).status >= 400
  );
}

export const FETCH_CALCULATOR_PARAMETERS = createAsyncAction(
  'product/FETCH_CALCULATOR_PARAMETERS/request',
  'product/FETCH_CALCULATOR_PARAMETERS/success',
  'product/FETCH_CALCULATOR_PARAMETERS/failure',
  'product/FETCH_CALCULATOR_PARAMETERS/cancel',
)<void, CalculatorParameters, Response | Error, void>();

export const calculatorParametersError = createReducer<Response | Error | null>(null)
  .handleAction([FETCH_CALCULATOR_PARAMETERS.failure], (_, { payload }) => payload)
  .handleAction(
    [FETCH_CALCULATOR_PARAMETERS.request, FETCH_CALCULATOR_PARAMETERS.success, RESET],
    () => null,
  );

export const calculatorParametersLoading = createReducer(false)
  .handleAction([FETCH_CALCULATOR_PARAMETERS.request], () => true)
  .handleAction(
    [
      FETCH_CALCULATOR_PARAMETERS.success,
      FETCH_CALCULATOR_PARAMETERS.failure,
      FETCH_CALCULATOR_PARAMETERS.cancel,
      RESET,
    ],
    () => false,
  );

export const calculatorParameters = createReducer<CalculatorParameters | null>(null)
  .handleAction([FETCH_CALCULATOR_PARAMETERS.failure, FETCH_CALCULATOR_PARAMETERS.cancel, RESET], () => null)
  .handleAction(FETCH_CALCULATOR_PARAMETERS.success, (_, { payload }) => payload);

export function* getCalculatorParameters() {
  const isCalcAvailable: boolean = yield select(isCalculatorAvailable);
  const cookies: { [x: string]: string } = yield select(getAllCookies);
  const projectConfigurationId = cookies[LAMINATE_PROJECT_CONFIGURATION_ID_COOKIE_NAME];

  if (isCalcAvailable && projectConfigurationId) {
    yield call(doFetchCalculatorParameters);
  }
}

export function* doFetchCalculatorParameters() {
  const productId: string = yield select(getArticle);
  const regionId: string = yield select(getRegionId);
  const abortController = (yield call([api, 'abortController'])) as EffectR.Call<
    (typeof api)['abortController']
  >;

  yield put(FETCH_CALCULATOR_PARAMETERS.request());
  const { requestID } = (yield select(getEnv)) as EffectR.Select<typeof getEnv>;
  yield call(log.info, 'Product-calculator-parameters was requested', requestID);

  try {
    const data = (yield call(
      fetchProductCalculatorParameters,
      productId,
      regionId,
      abortController.token,
    )) as EffectR.Call<typeof fetchProductCalculatorParameters>;
    if (isErrorResponse(data)) {
      throw new Error(`API Error ${data.status}: ${data.statusText || 'Unknown error'}`);
    }
    yield put(FETCH_CALCULATOR_PARAMETERS.success(data));
    yield call(log.info, 'Product-calculator-parameters was loaded', requestID);
  } catch (e) {
    if (e instanceof Error) {
      yield put(FETCH_CALCULATOR_PARAMETERS.failure(e));
      yield call(log.info, 'Product-calculator-parameters has error', requestID, e);
    }
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
      yield call(log.info, 'Product-calculator-parameters was cancelled', requestID);
      yield put(FETCH_CALCULATOR_PARAMETERS.cancel());
    }
  }
}
